import React, { useEffect, useMemo, useState } from 'react';
import { ArchiveBoxIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import BaseInput from '../../components/ui/base-input';
import { filterFolderTree, type AtsFolderNode } from '../utils/folders';

const expandablePaths = (nodes: AtsFolderNode[]): string[] => (
  nodes.flatMap((node) => (node.children.length ? [node.path, ...expandablePaths(node.children)] : []))
);

export const FolderMoveModal = ({
  folders,
  currentFolder,
  disabled,
  onClose,
  onSubmit,
  title = 'Переместить резюме',
  description = 'Выберите вакансию в структуре Страна - Проект - Вакансия.',
  selectionLabel = 'Новая папка',
  submitLabel = 'Переместить сюда',
  isSelectable = (node) => node.children.length === 0,
}: {
  folders: AtsFolderNode[];
  currentFolder: string;
  disabled: boolean;
  onClose: () => void;
  onSubmit: (folderPath: string) => void;
  title?: string;
  description?: string;
  selectionLabel?: string;
  submitLabel?: string;
  isSelectable?: (node: AtsFolderNode) => boolean;
}) => {
  const [selectedFolder, setSelectedFolder] = useState(currentFolder);
  const [query, setQuery] = useState('');
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(
    () => new Set(expandablePaths(folders)),
  );
  const visibleFolders = useMemo(() => filterFolderTree(folders, query), [folders, query]);
  const selectedNode = useMemo(() => {
    const find = (nodes: AtsFolderNode[]): AtsFolderNode | undefined => {
      for (const node of nodes) {
        if (node.path === selectedFolder) return node;
        const nested = find(node.children);
        if (nested) return nested;
      }
      return undefined;
    };
    const node = find(folders);
    return node;
  }, [folders, selectedFolder]);
  const selectedIsAllowed = Boolean(selectedNode && isSelectable(selectedNode));

  useEffect(() => {
    setSelectedFolder(currentFolder);
    setExpandedPaths(new Set(expandablePaths(folders)));
  }, [currentFolder, folders]);

  const renderNode = (node: AtsFolderNode, level = 0): React.ReactNode => {
    const hasChildren = node.children.length > 0;
    const expanded = Boolean(query.trim()) || expandedPaths.has(node.path);
    const selectable = isSelectable(node);
    const selected = selectedFolder === node.path;
    const current = currentFolder === node.path;
    return (
      <div key={node.path}>
        <div className={`group flex min-h-10 items-center gap-2 rounded-[8px] pr-2 ${selected ? 'bg-[#FFF2EC] text-[#C94F32]' : 'text-[#23252D] hover:bg-[#F5F4EF]'}`} style={{ paddingLeft: `${8 + level * 20}px` }}>
          {hasChildren ? <button type="button" onClick={() => setExpandedPaths((paths) => { const next = new Set(paths); if (next.has(node.path)) next.delete(node.path); else next.add(node.path); return next; })} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-[#868C98] hover:bg-white" aria-label={expanded ? `Свернуть ${node.name}` : `Развернуть ${node.name}`}><ChevronDownIcon className={`h-4 w-4 transition-transform ${expanded ? '' : '-rotate-90'}`} /></button> : <span className="w-7 shrink-0" />}
          <ArchiveBoxIcon className="h-4 w-4 shrink-0 text-[#F59E0B]" />
          <button type="button" onClick={() => selectable && setSelectedFolder(node.path)} disabled={!selectable} className={`min-w-0 flex-1 truncate py-2 text-left text-[13px] ${selectable ? 'cursor-pointer' : 'cursor-default font-semibold'}`} title={node.path}>{node.name}</button>
          <span className="shrink-0 text-[12px] text-[#868C98]">{node.count}</span>
          {current && <span className="hidden shrink-0 text-[11px] text-[#868C98] sm:inline">текущая</span>}
        </div>
        {hasChildren && expanded && node.children.map((child) => renderNode(child, level + 1))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/35 p-4">
      <div role="dialog" aria-modal="true" className="w-full max-w-[620px] overflow-hidden rounded-[16px] bg-white shadow-[0_28px_80px_rgba(21,22,24,0.26)]">
        <div className="flex items-center justify-between border-b border-[#EEF0F2] px-6 py-5">
          <div>
            <h2 className="text-[18px] font-bold text-[#23252D]">{title}</h2>
            <p className="mt-1 text-[13px] text-[#868C98]">{description}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-[#DDDCD6] text-[#525866] hover:bg-[#F5F4EF]" aria-label="Закрыть">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 px-6 py-5">
          <BaseInput size="sm">
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Найти страну, проект или вакансию" aria-label="Поиск папки для переноса" autoFocus className="w-full bg-transparent text-[14px] text-[#23252D] outline-none placeholder:text-[#A0A4AE]" />
          </BaseInput>
          <div className="rounded-[10px] border border-[#E7E8EC] bg-white p-2">
            <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-[#868C98]">Папки</div>
            <div className="max-h-[360px] overflow-y-auto pr-1">
              {visibleFolders.map((node) => renderNode(node))}
              {!folders.length && <div className="px-2 py-5 text-center text-[13px] text-[#868C98]">Папок пока нет</div>}
              {folders.length > 0 && visibleFolders.length === 0 && <div className="px-2 py-5 text-center text-[13px] text-[#868C98]">Папки не найдены</div>}
            </div>
          </div>
          <div className="rounded-[8px] bg-[#F8F7F3] px-3 py-2 text-[13px] text-[#525866]">
            {selectionLabel}: <span className="font-semibold text-[#23252D]">{selectedIsAllowed ? selectedNode?.name : 'не выбран'}</span>
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-[#EEF0F2] px-6 py-4">
          <button type="button" onClick={onClose} className="h-11 rounded-[9px] bg-[#F1F0EC] px-5 text-[14px] font-semibold text-[#77736B] hover:bg-[#E9E8E2]">Отмена</button>
          <button type="button" disabled={disabled || !selectedIsAllowed || selectedFolder === currentFolder} onClick={() => onSubmit(selectedFolder)} className="h-11 rounded-[9px] bg-[#E16349] px-5 text-[14px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#D8D6CF]">{disabled ? 'Перенос...' : submitLabel}</button>
        </div>
      </div>
    </div>
  );
};
